import React, { useState, useEffect } from "react";
import "./HorarioAtencionModal.css";
import { getPerfilMedico, updatePerfilMedico } from "../../../../../services/usuarios";
import { toast } from "react-toastify";

// Días visibles en la UI
const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

// Opciones fijas cada 30 minutos
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 ? "30" : "00";
  return `${h}:${m}`;
});

// Plantilla inicial (visual por defecto)
const INITIAL = {
  Lunes:     [{ start: "09:00", end: "18:00" }],
  Martes:    [{ start: "09:00", end: "18:00" }],
  Miércoles: [{ start: "09:00", end: "18:00" }],
  Jueves:    [{ start: "09:00", end: "18:00" }],
  Viernes:   [{ start: "09:00", end: "18:00" }],
  Sábado:    [],
  Domingo:   [],
};

const DEFAULT_MENSAJE_RECORDATORIO = {
  incluir_saludo_paciente: true,
  saludo: "Buen día",
  texto_1: "le recordamos que tiene una cita programada",
  incluir_texto_2: true,
  texto_2: "Por favor confirme su asistencia, gracias.",
  formato_hora: "24h",
};

const DEFAULT_MENSAJE_CONFIRMACION = {
  incluir_saludo_paciente: true,
  saludo: "Buen día",
  texto_1: "su cita quedó agendada",
  incluir_texto_2: true,
  texto_2: "Por favor confirme su asistencia, gracias.",
  formato_hora: "24h",
};

const INITIAL_RECORDATORIOS = {
  recordatorios_activos: false,
  confirmacion_activa: false,
  recordatorio_activo: false,
  canal: "",
  modo_envio: "",
  modo: "",
  telefono_remitente: "",
  whatsapp_conectado: false,
  mensaje_confirmacion: DEFAULT_MENSAJE_CONFIRMACION,
  mensaje_recordatorio: DEFAULT_MENSAJE_RECORDATORIO,
};


// Agendamiento en línea (página pública). Mismos valores por defecto que el backend.
const DEFAULT_CONFIG_AGENDA = {
  duracion_minutos: 60,
  dias_adelante: 15,
  anticipacion_minima_horas: 12,
};


// Helpers de tiempo (HH:MM ⇄ minutos)
const toMins = (hhmm) => {
  const [H, M] = (hhmm || "00:00").split(":").map(Number);
  return H * 60 + M;
};
const fromMins = (mins) => {
  const clamped = Math.max(0, Math.min(1440, mins));
  const H = String(Math.floor(clamped / 60)).padStart(2, "0");
  const M = String(clamped % 60).padStart(2, "0");
  return `${H}:${M}`;
};
const plus30 = (hhmm) => fromMins(toMins(hhmm) + 30);

// Normaliza un arreglo de rangos: ordena, quita solapes y asegura ≥30'
const normalizeRanges = (ranges) => {
  if (!Array.isArray(ranges)) return [];
  // 1) ordenar por inicio
  const sorted = [...ranges]
    .map(r => ({ start: r.start, end: r.end }))
    .sort((a, b) => toMins(a.start) - toMins(b.start));

  const out = [];
  for (let i = 0; i < sorted.length && out.length < 4; i++) {
    let s = sorted[i].start || "00:00";
    let e = sorted[i].end || plus30(s);

    // asegurar duración mínima 30'
    if (toMins(e) <= toMins(s)) e = plus30(s);

    // si hay anterior, empujar para que no se pegue/solape
    if (out.length > 0) {
      const prevEnd = out[out.length - 1].end;
      if (toMins(s) <= toMins(prevEnd)) {
        s = plus30(prevEnd);            // inicio = fin previo +30
        e = fromMins(toMins(s) + 30);   // asegurar 30'
      }
    }

    // clamp al día
    if (toMins(s) >= 1440) continue;
    if (toMins(e) > 1440) e = "24:00";
    if (toMins(e) - toMins(s) < 30) continue;

    out.push({ start: s, end: e });
  }
  return out;
};

export default function HorarioAtencionModal({ open = false, onClose }) {
  const [draft, setDraft] = useState(INITIAL);
  const [recordatorios, setRecordatorios] = useState(INITIAL_RECORDATORIOS);
    const [agendaPublica, setAgendaPublica] = useState({
    disponible: false,
    habilitado: false,
    slugGuardado: "",   // el link que ya tiene asignado
    slugSugerido: "",   // el que se le asignará al guardar (si aún no tiene)
    config: DEFAULT_CONFIG_AGENDA,
  });
  // Ficha previa: disponible = permiso de Salvir, habilitada = switch del médico
  const [fichaPrevia, setFichaPrevia] = useState({ disponible: false, habilitada: false });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // "error" | null
  const [editandoConfirmacion, setEditandoConfirmacion] = useState(false);
  const [editandoRecordatorio, setEditandoRecordatorio] = useState(false);

  // Carga inicial desde /perfil/ (cae a INITIAL si viene vacío)
  useEffect(() => {
    if (!open) return;
    setEditandoConfirmacion(false);
    setEditandoRecordatorio(false);
    (async () => {
      setLoading(true);
      setStatus(null);
      try {
        const data = await getPerfilMedico(); // GET /api/perfil/
        const h = data?.horario;
        const isEmptyHorario = h && typeof h === "object" && Object.keys(h).length === 0;
        setDraft(!h || isEmptyHorario ? INITIAL : h);

        const r = data?.config_recordatorios;
        const isEmptyRecordatorios = r && typeof r === "object" && Object.keys(r).length === 0;
       setRecordatorios(
          !r || isEmptyRecordatorios
            ? INITIAL_RECORDATORIOS
            : {
                ...INITIAL_RECORDATORIOS,
                ...r,
                mensaje_confirmacion: {
                  ...DEFAULT_MENSAJE_CONFIRMACION,
                  ...(r.mensaje_confirmacion || {}),
                },
                mensaje_recordatorio: {
                  ...DEFAULT_MENSAJE_RECORDATORIO,
                  ...(r.mensaje_recordatorio || {}),
                },
              }
        );
               
        setAgendaPublica({
          disponible: !!data?.autoagendamiento_disponible,
          habilitado: !!data?.autoagendamiento_habilitado,
          slugGuardado: data?.booking_slug || "",
          slugSugerido: data?.booking_slug_sugerido || "",
          config: { ...DEFAULT_CONFIG_AGENDA, ...(data?.config_autoagendamiento || {}) },
        });
        setFichaPrevia({
          disponible: !!data?.ficha_previa_disponible,
          habilitada: !!data?.ficha_previa_habilitada,
        });

      } catch {
        setDraft(INITIAL);
        setStatus("error");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!open) return null;



  // Activa/Desactiva un día completo
  const toggleDay = (dia, checked) => {
    setDraft(prev => ({
      ...prev,
      [dia]: checked ? (prev[dia]?.length ? normalizeRanges(prev[dia]) : [{ start: "09:00", end: "09:30" }]) : []
    }));
  };

  // Actualiza un campo (start/end) de un rango con auto-avance
  const updateRange = (dia, idx, field, value) => {
    setDraft(prev => {
      const ranges = prev[dia] ? [...prev[dia]] : [];
      if (!ranges[idx]) return prev;

      const r = { ...ranges[idx] };
      r[field] = value;

      // Auto-avance mínimo de 30’ en el mismo rango
      if (field === "start" && toMins(r.end) <= toMins(r.start)) {
        r.end = plus30(r.start);
      }
      if (field === "end" && toMins(r.end) <= toMins(r.start)) {
        r.start = fromMins(toMins(r.end) - 30);
      }

      ranges[idx] = r;
      const fixed = normalizeRanges(ranges);
      return { ...prev, [dia]: fixed };
    });
  };

  // Agrega un rango nuevo (máx 4), inicia +30 del fin anterior, dura 30'
  const addRange = (dia) => {
    setDraft(prev => {
      const ranges = prev[dia] ? [...prev[dia]] : [];
      if (ranges.length >= 4) return prev;

      if (ranges.length === 0) {
        ranges.push({ start: "09:00", end: "09:30" });
      } else {
        const last = ranges[ranges.length - 1];
        const start = plus30(last.end);
        const end = plus30(start);
        ranges.push({ start, end });
      }
      return { ...prev, [dia]: normalizeRanges(ranges) };
    });
  };

  // Elimina un rango específico del día
  const deleteRange = (dia, idx) => {
    setDraft(prev => {
      const copy = prev[dia] ? [...prev[dia]] : [];
      copy.splice(idx, 1);
      return { ...prev, [dia]: normalizeRanges(copy) };
    });
  };

  const getMensajeRecordatorio = () => ({
  ...DEFAULT_MENSAJE_RECORDATORIO,
  ...(recordatorios.mensaje_recordatorio || {}),
});

const updateMensajeRecordatorio = (field, value) => {
  setRecordatorios((prev) => ({
    ...prev,
    mensaje_recordatorio: {
      ...DEFAULT_MENSAJE_RECORDATORIO,
      ...(prev.mensaje_recordatorio || {}),
      [field]: value,
    },
  }));
};

const getMensajeConfirmacion = () => ({
  ...DEFAULT_MENSAJE_CONFIRMACION,
  ...(recordatorios.mensaje_confirmacion || {}),
});

const updateMensajeConfirmacion = (field, value) => {
  setRecordatorios((prev) => ({
    ...prev,
    mensaje_confirmacion: {
      ...DEFAULT_MENSAJE_CONFIRMACION,
      ...(prev.mensaje_confirmacion || {}),
      [field]: value,
    },
  }));
};

const formatearHoraPreview = (mensaje) => {
  if (mensaje.formato_hora === "12h") {
    return "2:30 PM";
  }

  return "14:30 hrs";
};

const construirPreviewMensaje = (mensaje, defaultMensaje) => {
  return (
    <>
      {mensaje.incluir_saludo_paciente && (
        <>
          {mensaje.saludo || "Hola"}{" "}
          <span className="preview-variable">[Nombre del paciente]</span>,{" "}
        </>
      )}

      {mensaje.texto_1 || defaultMensaje.texto_1} el{" "}
      <span className="preview-variable">[fecha de la cita]</span> a las{" "}
      <span className="preview-variable">[{formatearHoraPreview(mensaje)}]</span>.

      {mensaje.incluir_texto_2 && mensaje.texto_2 && (
        <> {mensaje.texto_2}</>
      )}
    </>
  );
};

  const handleSave = async () => {
  try {
    const mensajeConfirmacion = getMensajeConfirmacion();
    const mensajeRecordatorio = getMensajeRecordatorio();

     if (recordatorios.recordatorios_activos) {
      if (!mensajeConfirmacion.texto_1.trim()) {
        toast.error("El texto principal de la confirmación no puede quedar vacío.");
        return;
      }

      if (!mensajeRecordatorio.texto_1.trim()) {
        toast.error("El texto principal del recordatorio no puede quedar vacío.");
        return;
      }
    }

    const recordatoriosFinal = recordatorios.recordatorios_activos
      ? {
          ...recordatorios,
          canal: "whatsapp",
          modo_envio: "semi_auto",
          modo: "24h_antes",
          mensaje_confirmacion: mensajeConfirmacion,
          mensaje_recordatorio: mensajeRecordatorio,
        }
      : {
          ...recordatorios,
          canal: "",
          modo_envio: "",
          modo: "",
          mensaje_confirmacion: mensajeConfirmacion,
          mensaje_recordatorio: mensajeRecordatorio,
        };



    await updatePerfilMedico({
      horario: draft,
      config_recordatorios: recordatoriosFinal,
      autoagendamiento_habilitado: agendaPublica.habilitado,
      config_autoagendamiento: agendaPublica.config,
      ficha_previa_habilitada: fichaPrevia.habilitada,
    });



    onClose();
  } catch {
    toast.error("No se pudo guardar la configuración. Intenta de nuevo.");
  }
};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-schedule" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-schedule__header">
          <h3 className="modal-schedule__title">Configuración de horarios de atención y WhatsApp para citas</h3>
          <button className="modal-schedule__close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        {/* Body */}
        <div className="modal-schedule__body">
          {loading && <p>Cargando...</p>}
          {status === "error" && <p className="status error">Ocurrió un error</p>}

          {DIAS.map((dia) => {
            const dayRanges = draft[dia] || [];
            const activo = dayRanges.length > 0;

            return (
              <div key={dia} className="day-row">
                <div className="day-row__head">
                  {/* Switch tipo iPhone */}
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={activo}
                      onChange={(e) => toggleDay(dia, e.target.checked)}
                    />
                    <span className="switch__slider switch__slider--round"></span>
                  </label>

                  <span className="day-row__name">{dia}</span>
                </div>

                {/* Rangos del día */}
                {activo && (
                  <div className="day-row__ranges">
                    {dayRanges.map((r, idx) => (
                      <div key={idx} className="range-row">
                        {/* Inicio */}
                        <select
                          className="range-row__time"
                          value={r.start}
                          onChange={(e) => updateRange(dia, idx, "start", e.target.value)}
                        >
                          {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>

                        <span className="range-row__dash">—</span>

                        {/* Fin */}
                        <select
                          className="range-row__time"
                          value={r.end}
                          onChange={(e) => updateRange(dia, idx, "end", e.target.value)}
                        >
                          {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>

                        <button
                          className="range-row__delete"
                          onClick={() => deleteRange(dia, idx)}
                          aria-label="Eliminar rango"
                          title="Eliminar rango"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}

                    <button
                      className="ranges-add"
                      onClick={() => addRange(dia)}
                      disabled={(draft[dia] || []).length >= 4}
                    >
                      + Agregar rango
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
            <div className="reminders-section">
                <h4 className="reminders-section__title">WhatsApp para citas</h4>

                  <p className="reminders-section__hint">
                      Para enviar confirmaciones o recordatorios por WhatsApp, la cita debe estar ligada a un paciente con teléfono celular válido. También asegúrate de tener WhatsApp abierto o sincronizado en el dispositivo desde el que enviarás el mensaje.
                    </p>

                    <div className="reminder-row">
                      <div>
                        <strong>Confirmación por WhatsApp</strong>
                        <p> Envía al paciente la confirmación de su cita al guardarla. También podrás reenviarla desde la cita durante las primeras horas si necesitas volver a compartirla.</p>
                      </div>

                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={recordatorios.confirmacion_activa}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            setRecordatorios((prev) => {
                              const algunoActivo = checked || prev.recordatorio_activo;

                              return {
                                ...prev,
                                confirmacion_activa: checked,
                                recordatorios_activos: algunoActivo,
                                canal: algunoActivo ? "whatsapp" : "",
                                modo_envio: algunoActivo ? "semi_auto" : "",
                                modo: algunoActivo ? "24h_antes" : "",
                              };
                            });
                          }}
                        />
                        <span className="switch__slider switch__slider--round"></span>
                      </label>
                    </div>
                    {recordatorios.confirmacion_activa && (
                          <div className="reminders-message-box">
                            <div className="reminders-message-box__header">
                              <h5 className="reminders-message-box__title">
                                Mensaje de confirmación
                              </h5>

                             <button
                                type="button"
                                className="reminders-message-box__edit-button"
                                onClick={() => setEditandoConfirmacion((prev) => !prev)}
                              >
                                {editandoConfirmacion ? "Ocultar edición" : "Editar mensaje"}
                              </button>
                            </div>

                            <div className="reminders-message-preview">
                              <strong>Vista previa:</strong>
                              <p>
                                {construirPreviewMensaje(
                                  getMensajeConfirmacion(),
                                  DEFAULT_MENSAJE_CONFIRMACION
                                )}
                              </p>
                            </div>

                            {editandoConfirmacion && (
                              <div className="reminders-message-editor">

                                {/* BLOQUE 1 */}
                                <div className="reminders-message-segment">
                                  <div className="reminders-message-segment__head">
                                    <div>
                                      <strong>1. Saludo y paciente</strong>
                                      <p>
                                        Si está activo, el mensaje inicia con el saludo y el nombre del paciente.
                                      </p>
                                    </div>

                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        checked={getMensajeConfirmacion().incluir_saludo_paciente}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const mensaje = getMensajeConfirmacion();

                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_confirmacion: {
                                              ...DEFAULT_MENSAJE_CONFIRMACION,
                                              ...(prev.mensaje_confirmacion || {}),
                                              incluir_saludo_paciente: checked,
                                              saludo: checked
                                                ? (mensaje.saludo?.trim() || DEFAULT_MENSAJE_CONFIRMACION.saludo)
                                                : mensaje.saludo,
                                            },
                                          }));
                                        }}
                                      />
                                      <span className="switch__slider switch__slider--round"></span>
                                    </label>
                                  </div>

                                  {getMensajeConfirmacion().incluir_saludo_paciente && (
                                    <input
                                      className="reminders-section__input"
                                      type="text"
                                      value={getMensajeConfirmacion().saludo}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        if (!value.trim()) {
                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_confirmacion: {
                                              ...DEFAULT_MENSAJE_CONFIRMACION,
                                              ...(prev.mensaje_confirmacion || {}),
                                              saludo: "",
                                              incluir_saludo_paciente: false,
                                            },
                                          }));
                                          return;
                                        }

                                        updateMensajeConfirmacion("saludo", value);
                                      }}
                                      placeholder="Ej. Buen día"
                                    />
                                  )}
                                </div>

                                {/* BLOQUE 2 */}
                                <div className="reminders-message-segment">
                                  <div className="reminders-message-segment__head">
                                    <div>
                                      <strong>2. Texto principal y horario</strong>
                                      <p>
                                        Este bloque siempre aparece. La fecha y hora se agregan automáticamente.
                                      </p>
                                    </div>
                                  </div>

                                  <textarea
                                    className="reminders-section__textarea"
                                    value={getMensajeConfirmacion().texto_1}
                                    onChange={(e) =>
                                      updateMensajeConfirmacion("texto_1", e.target.value)
                                    }
                                    placeholder="Ej. su cita quedó agendada"
                                    rows={2}
                                  />

                                  <select
                                    className="reminders-section__select reminders-message-hour-select"
                                    value={getMensajeConfirmacion().formato_hora}
                                    onChange={(e) =>
                                      updateMensajeConfirmacion("formato_hora", e.target.value)
                                    }
                                  >
                                    <option value="24h">24 horas — 14:30 hrs</option>
                                    <option value="12h">12 horas — 2:30 PM</option>
                                  </select>
                                </div>

                                {/* BLOQUE 3 */}
                                <div className="reminders-message-segment">
                                  <div className="reminders-message-segment__head">
                                    <div>
                                      <strong>3. Texto final</strong>
                                      <p>
                                        Cierre opcional del mensaje.
                                      </p>
                                    </div>

                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        checked={getMensajeConfirmacion().incluir_texto_2}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const mensaje = getMensajeConfirmacion();

                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_confirmacion: {
                                              ...DEFAULT_MENSAJE_CONFIRMACION,
                                              ...(prev.mensaje_confirmacion || {}),
                                              incluir_texto_2: checked,
                                              texto_2: checked
                                                ? (mensaje.texto_2?.trim() || DEFAULT_MENSAJE_CONFIRMACION.texto_2)
                                                : mensaje.texto_2,
                                            },
                                          }));
                                        }}
                                      />
                                      <span className="switch__slider switch__slider--round"></span>
                                    </label>
                                  </div>

                                  {getMensajeConfirmacion().incluir_texto_2 && (
                                    <textarea
                                      className="reminders-section__textarea"
                                      value={getMensajeConfirmacion().texto_2}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        if (!value.trim()) {
                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_confirmacion: {
                                              ...DEFAULT_MENSAJE_CONFIRMACION,
                                              ...(prev.mensaje_confirmacion || {}),
                                              texto_2: "",
                                              incluir_texto_2: false,
                                            },
                                          }));
                                          return;
                                        }

                                        updateMensajeConfirmacion("texto_2", value);
                                      }}
                                      placeholder="Ej. Por favor confirme su asistencia, gracias."
                                      rows={2}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          )}

                    <div className="reminder-row">
                      <div>
                        <strong>Recordatorio por WhatsApp</strong>
                        <p> Aparece en el panel de recordatorios un día antes de la cita, de 7:00 a 21:00. También podrás enviarlo desde la cita después de la confirmación, siempre que aún falten más de 2 horas para que inicie.</p>
                      </div>

                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={recordatorios.recordatorio_activo}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            setRecordatorios((prev) => {
                              const algunoActivo = prev.confirmacion_activa || checked;

                              return {
                                ...prev,
                                recordatorio_activo: checked,
                                recordatorios_activos: algunoActivo,
                                canal: algunoActivo ? "whatsapp" : "",
                                modo_envio: algunoActivo ? "semi_auto" : "",
                                modo: algunoActivo ? "24h_antes" : "",
                              };
                            });
                          }}
                        />
                        <span className="switch__slider switch__slider--round"></span>
                      </label>
                    </div>
                   
                      <>
                       
                           {recordatorios.recordatorio_activo && (
                          <div className="reminders-message-box">
                            <div className="reminders-message-box__header">
                              <h5 className="reminders-message-box__title">
                                Mensaje del recordatorio
                              </h5>

                              <button
                                type="button"
                                className="reminders-message-box__edit-button"
                                onClick={() => setEditandoRecordatorio((prev) => !prev)}
                              >
                                {editandoRecordatorio ? "Ocultar edición" : "Editar mensaje"}
                              </button>
                            </div>

                            <div className="reminders-message-preview">
                              <strong>Vista previa:</strong>
                              <p>
                                {construirPreviewMensaje(
                                  getMensajeRecordatorio(),
                                  DEFAULT_MENSAJE_RECORDATORIO
                                )}
                              </p>
                            </div>

                            {editandoRecordatorio && (
                              <div className="reminders-message-editor">

                                {/* BLOQUE 1 */}
                                <div className="reminders-message-segment">
                                  <div className="reminders-message-segment__head">
                                    <div>
                                      <strong>1. Saludo y paciente</strong>
                                      <p>
                                        Si está activo, el mensaje inicia con el saludo y el nombre del paciente.
                                      </p>
                                    </div>

                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        checked={getMensajeRecordatorio().incluir_saludo_paciente}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const mensaje = getMensajeRecordatorio();

                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_recordatorio: {
                                              ...DEFAULT_MENSAJE_RECORDATORIO,
                                              ...(prev.mensaje_recordatorio || {}),
                                              incluir_saludo_paciente: checked,
                                              saludo: checked
                                                ? (mensaje.saludo?.trim() || DEFAULT_MENSAJE_RECORDATORIO.saludo)
                                                : mensaje.saludo,
                                            },
                                          }));
                                        }}
                                      />
                                      <span className="switch__slider switch__slider--round"></span>
                                    </label>
                                  </div>

                                  {getMensajeRecordatorio().incluir_saludo_paciente && (
                                    <input
                                      className="reminders-section__input"
                                      type="text"
                                      value={getMensajeRecordatorio().saludo}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        if (!value.trim()) {
                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_recordatorio: {
                                              ...DEFAULT_MENSAJE_RECORDATORIO,
                                              ...(prev.mensaje_recordatorio || {}),
                                              saludo: "",
                                              incluir_saludo_paciente: false,
                                            },
                                          }));
                                          return;
                                        }

                                        updateMensajeRecordatorio("saludo", value);
                                      }}
                                      placeholder="Ej. Buen día"
                                    />
                                  )}
                                </div>

                                {/* BLOQUE 2 */}
                                <div className="reminders-message-segment">
                                  <div className="reminders-message-segment__head">
                                    <div>
                                      <strong>2. Texto principal y horario</strong>
                                      <p>
                                        Este bloque siempre aparece. La fecha y hora se agregan automáticamente.
                                      </p>
                                    </div>
                                  </div>

                                  <textarea
                                    className="reminders-section__textarea"
                                    value={getMensajeRecordatorio().texto_1}
                                    onChange={(e) =>
                                      updateMensajeRecordatorio("texto_1", e.target.value)
                                    }
                                    placeholder="Ej. le recordamos que tiene una cita programada"
                                    rows={2}
                                  />

                                  <select
                                    className="reminders-section__select reminders-message-hour-select"
                                    value={getMensajeRecordatorio().formato_hora}
                                    onChange={(e) =>
                                      updateMensajeRecordatorio("formato_hora", e.target.value)
                                    }
                                  >
                                    <option value="24h">24 horas — 14:30 hrs</option>
                                    <option value="12h">12 horas — 2:30 PM</option>
                                  </select>
                                </div>

                                {/* BLOQUE 3 */}
                                <div className="reminders-message-segment">
                                  <div className="reminders-message-segment__head">
                                    <div>
                                      <strong>3. Texto final</strong>
                                      <p>
                                        Cierre opcional del mensaje.
                                      </p>
                                    </div>

                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        checked={getMensajeRecordatorio().incluir_texto_2}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const mensaje = getMensajeRecordatorio();

                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_recordatorio: {
                                              ...DEFAULT_MENSAJE_RECORDATORIO,
                                              ...(prev.mensaje_recordatorio || {}),
                                              incluir_texto_2: checked,
                                              texto_2: checked
                                                ? (mensaje.texto_2?.trim() || DEFAULT_MENSAJE_RECORDATORIO.texto_2)
                                                : mensaje.texto_2,
                                            },
                                          }));
                                        }}
                                      />
                                      <span className="switch__slider switch__slider--round"></span>
                                    </label>
                                  </div>

                                  {getMensajeRecordatorio().incluir_texto_2 && (
                                    <textarea
                                      className="reminders-section__textarea"
                                      value={getMensajeRecordatorio().texto_2}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        if (!value.trim()) {
                                          setRecordatorios((prev) => ({
                                            ...prev,
                                            mensaje_recordatorio: {
                                              ...DEFAULT_MENSAJE_RECORDATORIO,
                                              ...(prev.mensaje_recordatorio || {}),
                                              texto_2: "",
                                              incluir_texto_2: false,
                                            },
                                          }));
                                          return;
                                        }

                                        updateMensajeRecordatorio("texto_2", value);
                                      }}
                                      placeholder="Ej. Por favor confirme su asistencia, gracias."
                                      rows={2}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                )}
              </>
            
          </div>
         
        {/* Agendamiento en línea (página pública) */}
        {agendaPublica.disponible && (
          <div className="reminders-section">
            <h4 className="reminders-section__title">Agendamiento en línea</h4>

            <div className="reminder-row">
              <div>
                <strong>Página pública de agendamiento</strong>
                <p>
                   Al activar esta opción, podrás compartir un enlace para que tus pacientes
                    agenden sus citas directamente. Podrán elegir entre los horarios disponibles
                    y completar sus datos antes de la consulta. Las citas aparecerán
                    automáticamente en tu agenda, junto con la información proporcionada
                    por cada paciente.
                </p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={agendaPublica.habilitado}
                  onChange={(e) =>
                    setAgendaPublica((prev) => ({ ...prev, habilitado: e.target.checked }))
                  }
                />
                <span className="switch__slider switch__slider--round"></span>
              </label>
            </div>

            {agendaPublica.habilitado && (
              <div className="reminders-message-box">
                <label>
                  Duración de cada cita
                  <select
                    className="reminders-section__select"
                    value={agendaPublica.config.duracion_minutos}
                    onChange={(e) =>
                      setAgendaPublica((prev) => ({
                        ...prev,
                        config: { ...prev.config, duracion_minutos: Number(e.target.value) },
                      }))
                    }
                  >
                    <option value={30}>30 minutos</option>
                    <option value={45}>45 minutos</option>
                    <option value={60}>60 minutos</option>
                    <option value={90}>90 minutos</option>
                  </select>
                </label>

                <label>
                  ¿Hasta cuántos días hacia adelante pueden agendar?
                  <input
                    className="reminders-section__input"
                    type="number"
                    min={1}
                    max={180}
                    value={agendaPublica.config.dias_adelante}
                    onChange={(e) =>
                      setAgendaPublica((prev) => ({
                        ...prev,
                        config: { ...prev.config, dias_adelante: Number(e.target.value) || 1 },
                      }))
                    }
                  />
                </label>

                <label>
                  Tiempo mínimo antes de la cita con el que se puede reservar (horas)
                  <input
                    className="reminders-section__input"
                    type="number"
                    min={0}
                    max={72}
                    value={agendaPublica.config.anticipacion_minima_horas}
                    onChange={(e) =>
                      setAgendaPublica((prev) => ({
                        ...prev,
                        config: { ...prev.config, anticipacion_minima_horas: Number(e.target.value) || 0 },
                      }))
                    }
                  />
                </label>
                <div>
                  <strong>Tu link para compartir</strong>

                  {agendaPublica.slugGuardado ? (
                    <>
                      <p style={{ margin: "6px 0", wordBreak: "break-all" }}>
                        {`${window.location.origin}/agendar/${agendaPublica.slugGuardado}`}
                      </p>
                      <button
                        type="button"
                        className="reminders-message-box__edit-button"
                        onClick={async () => {
                          const link = `${window.location.origin}/agendar/${agendaPublica.slugGuardado}`;
                          try {
                            await navigator.clipboard.writeText(link);
                            toast.success("Link copiado.");
                          } catch {
                            window.prompt("Copia tu link:", link);
                          }
                        }}
                      >
                        Copiar mi link
                      </button>
                      <p className="reminders-section__hint">
                        Si necesitas cambiar tu link, contacta a soporte.
                      </p>
                    </>
                  ) : agendaPublica.slugSugerido ? (
                    <p className="reminders-section__hint">
                      Al guardar se te asignará este link:{" "}
                      {`${window.location.origin}/agendar/${agendaPublica.slugSugerido}`}
                    </p>
                  ) : (
                    <p className="reminders-section__hint">
                      No pudimos asignarte un link automáticamente. Contacta a soporte para que te asignen uno.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

                {/* Ficha previa a la consulta */}
        {fichaPrevia.disponible && (
          <div className="reminders-section">
            <h4 className="reminders-section__title">Ficha previa a la consulta</h4>

            <div className="reminder-row">
              <div>
                <strong>Enviar ficha previa al paciente</strong>
                <p>
                  Al activar esta opción, se incluirá un enlace en los mensajes de
                  confirmación y recordatorio de cita por WhatsApp para que el paciente
                  complete sus datos antes de la consulta. La información enviada
                  aparecerá en la cita como "Ficha recibida" y podrás revisarla
                  al registrar al paciente.
                </p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={fichaPrevia.habilitada}
                  onChange={(e) =>
                    setFichaPrevia((prev) => ({ ...prev, habilitada: e.target.checked }))
                  }
                />
                <span className="switch__slider switch__slider--round"></span>
              </label>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="modal-schedule__footer">
          <button className="btn btn--secondary" onClick={onClose}>Atrás</button>
          <button className="btn btn--primary" onClick={handleSave} disabled={loading}>
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
